import React, { useEffect, useState, useContext, useRef } from 'react';
import { useSnackbar } from 'notistack';

import { useAuth0 } from "@auth0/auth0-react";
import { LinearProgress } from '@mui/material';
import { DataGridPro, GridActionsCellItem } from '@mui/x-data-grid-pro';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useFetch } from '../components/auth/useFetch';
import UserContext from '../contexts/UserContext';
import ProjectContext from '../contexts/ProjectContext';

import LUTViewMenuBar from '../components/menubar/LUTViewMenuBar';
import ConfirmActionDialog from '../components/dialog/ConfirmActionDialog';
import LutTextField from '../components/textfield/LutTextField';

const formatDateTime = (value) => {
    try{
        if(value === null) throw ""
        const date = new Date(value)
        return Intl.DateTimeFormat(undefined, {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            dateStyle: 'short',
            timeStyle: 'short',
        }).format(date)
    }
    catch(e) {
        return "-"
    }
};

export default function LUTView(props) {
    const { setIsLoading } = props;
    const { isAuthenticated } = useAuth0();
    const { enqueueSnackbar } = useSnackbar();
    const { konsolUser } = useContext(UserContext);
    const user = `${konsolUser?.given_name ?? ''} ${konsolUser?.family_name ?? ''}`.trim();

    const fetchWithAuth = useFetch();
    const projectContext = useContext(ProjectContext);
    const saveResetTimerRef = useRef(null);

    const [rows, setRows] = useState([]);
    const [originalRows, setOriginalRows] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [rowPendingDelete, setRowPendingDelete] = useState(null);

    const [lutLoading, setLutLoading] = useState(false);
    const [formState, setFormState] = useState('none'); // none | unsaved | saving | saved | error
    const [saveStatus, setSaveStatus] = useState('\u00A0');
    const [saveStatusColor, setSaveStatusColor] = useState('black');
    const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);

    const requestDelete = (row) => {
        setRowPendingDelete(row);
        setConfirmOpen(true);
    };

    const updateRowField = (id, field, value) => {
        setRows(prev => {
            const existing = prev.find(r => r.id === id);
            if(!existing) return prev;
            if(existing[field] === value) return prev;
            const updated = prev.map(r => r.id === id ? { ...r, [field]: value } : r);
            recomputeUnsavedState(updated);
            return updated;
        });
    };

    const columns = [
        { field: 'name', headerName: 'Name', width: 200, sortable: false,
            renderCell: (params) => (
                <LutTextField
                    disabled={false}
                    currentValue={params.row?.name}
                    setNewValue={(val) => updateRowField(params.id, 'name', val)}
                />
            )
        },
        { field: 'description', headerName: 'Description', width: 300, sortable: false,
            renderCell: (params) => (
                <LutTextField
                    disabled={false}
                    currentValue={params.row?.description}
                    setNewValue={(val) => updateRowField(params.id, 'description', val)}
                />
            )
        },
        { field: 'filename',    headerName: 'File Name',        width: 250 },
        { field: 'user',        headerName: 'Submitted By',     width: 150 },
        { field: 'upload_time', headerName: 'Submission Time',  width: 250, valueFormatter: ({ value }) => formatDateTime(value)},
        { field: 'actions',     headerName: 'Delete LUT',       width: 100, type: 'actions',
            getActions: (params) => [
                <GridActionsCellItem
                    key="delete"
                    icon={<DeleteOutlineIcon color="error" />}
                    label="Delete LUT"
                    onClick={() => requestDelete(params.row)}
                    showInMenu={false}
                />
            ]
        }
    ];

    useEffect(() => {
        if(!isAuthenticated) return;
        if (rows.length === 0 && formState !== 'unsaved') {
            retrieveLuts();
        }
        if (formState === 'unsaved') return;
        const id = setInterval(() => { retrieveLuts(); }, 15000);
        return () => clearInterval(id);
    }, [isAuthenticated, formState]);

    useEffect(() => {
        if (saveResetTimerRef.current) {
            clearTimeout(saveResetTimerRef.current);
            saveResetTimerRef.current = null;
        }
        switch(formState){
            case 'unsaved':
                setSaveButtonDisabled(false);
                setSaveStatus('Unsaved Changes');
                setSaveStatusColor('orange');
                break;
            case 'saving':
                setSaveButtonDisabled(true);
                setSaveStatus('Saving');
                setSaveStatusColor('green');
                break;
            case 'saved':
                setSaveButtonDisabled(true);
                setSaveStatus('SAVED!');
                setSaveStatusColor('green');
                saveResetTimerRef.current = setTimeout(() => {
                    setFormState('none');
                }, 4000);
                break;
            case 'error':
                setSaveButtonDisabled(false);
                setSaveStatus('Error Saving');
                setSaveStatusColor('red');
                break;
            case 'none':
            default:
                setSaveButtonDisabled(true);
                setSaveStatus('\u00A0');
                setSaveStatusColor('black');
        }
        return () => {
            if (saveResetTimerRef.current) clearTimeout(saveResetTimerRef.current);
        }
    }, [formState]);

    const retrieveLuts = async () => {
        if (formState === 'unsaved') return; 
        try {
            setLutLoading(true)
            setIsLoading(true)
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/lut`, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-Project-ID': projectContext?.project_db
                },
            })
            const data = await response.json();
            const safeData = Array.isArray(data) ? data : [];
            setRows(safeData);
            if (formState !== 'unsaved') {
                setOriginalRows(JSON.parse(JSON.stringify(safeData)));
            }
        }
        catch(e){
            console.error(e)
        }
        finally {
            setLutLoading(false)
            setIsLoading(false)
        }
    }

    const performDelete = async () => {
        const row = rowPendingDelete;
        if(!row?.id && !row?.name) { setConfirmOpen(false); return; }
        try {
            setLutLoading(true);
            const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/konsol/lut`);
            if(row.id) url.searchParams.set('id', row.id);
            if(row.name) url.searchParams.set('name', row.name);
            if(user) url.searchParams.set('user', user);
            const resp = await fetchWithAuth(url.toString(), {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Project-ID': projectContext?.project_db
                }
            });
            if(!resp.ok){
                console.error('Failed to delete LUT', await resp.text());
                enqueueSnackbar('Failed to delete LUT', { variant: 'error' });
                return;
            }
            setRows(prev => prev.filter(r => {
                if(row.id) return r.id !== row.id;
                return r.name !== row.name; // fallback
            }));
            enqueueSnackbar('LUT deleted', { variant: 'success' });
        } catch(e){
            console.error(e);
            enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
        } finally {
            setLutLoading(false);
            setConfirmOpen(false);
            setRowPendingDelete(null);
        }
    };

    const saveChanges = async () => {
        const modified = diffModifiedRows();
        if(modified.length === 0){
            enqueueSnackbar('No changes to save', { variant: 'info' });
            return;
        }
        setFormState('saving');
        try {
            const results = await Promise.all(modified.map(row => {
                const original = originalRows.find(o => o.id === row.id) || {};
                const body = { id: row.id };
                if(original.name !== row.name) body.name = row.name;
                if((original.description || '') !== (row.description || '')) body.description = row.description || '';
                body.user = user;
                return fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/lut/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Project-ID': projectContext?.project_db
                    },
                    body: JSON.stringify(body)
                });
            }));
            const failed = [];
            for (let i = 0; i < results.length; i++) {
                const resp = results[i];
                if(!resp.ok){
                    console.error('Failed to patch LUT', modified[i].id, await resp.text());
                    failed.push(modified[i]);
                }
            }
            if(failed.length){
                enqueueSnackbar(`Failed to save ${failed.length} LUT${failed.length>1?'s':''}`, { variant: 'error' });
                setFormState('error');
                return;
            }
            setOriginalRows(JSON.parse(JSON.stringify(rows)));
            enqueueSnackbar('LUT changes saved', { variant: 'success' });
            setFormState('saved');
        } catch(e){
            console.error(e);
            enqueueSnackbar('Unexpected error while saving', { variant: 'error' });
            setFormState('error');
        }
    };

    const revertChanges = () => {
        setRows(JSON.parse(JSON.stringify(originalRows)));
        setFormState('none');
        enqueueSnackbar('Changes reverted', { variant: 'info' });
    };

    const recomputeUnsavedState = (nextRows) => {
        const hasDiff = nextRows.some(r => {
            const o = originalRows.find(or => or.id === r.id);
            if(!o) return false;
            return o.name !== r.name || (o.description || '') !== (r.description || '');
        });
        setFormState(hasDiff ? 'unsaved' : 'none');
    };

    const diffModifiedRows = () => {
        return rows.filter(r => {
            const o = originalRows.find(or => or.id === r.id);
            if(!o) return false;
            return o.name !== r.name || (o.description || '') !== (r.description || '');
        });
    };

    return (
        <React.Fragment>
            <LUTViewMenuBar 
                retrieveLuts={() => {
                    if(formState === 'unsaved'){
                        enqueueSnackbar('Save or revert changes before refreshing.', { variant: 'warning' });
                        return;
                    }
                    retrieveLuts();
                }}
                onSave={saveChanges}
                onRevert={revertChanges}
                saveDisabled={saveButtonDisabled}
                status={saveStatus}
                statusColor={saveStatusColor}
            />
            <DataGridPro
                sx={{
                    backgroundColor: '#101010', // Change the background color of the data grid
                    borderColor: '#122218',
                    '& .MuiDataGrid-root': {
                        cursor: 'pointer', // Change the cursor to pointer when hovering over the data grid
                    },
                    '& .MuiDataGrid-cell:hover': {
                        color: 'rgb(76, 146, 107)',
                    },
                    '& .MuiDataGrid-columnSeparator': {
                        borderColor: 'green', // Make the separator green
                        borderWidth: '2px', // Set the thickness of the separator
                        visibility: 'visible', // Ensure the separator always visible
                        '&:hover': {
                            borderColor: 'rgb(76, 146, 107)', // Ensure the separator stays green on hover
                        },
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#101010', // Change the column header background to black
                    },
                    '& .MuiDataGrid-columnHeader': {
                        fontSize: '14px', // Customize the font size of the column headers
                        color: 'white', // Change the column header color to white
                        fontWeight: 'bold', // Make the column header text bold
                        borderBottom: 'None', // Change the bottom line color of the column headers
                    },
                    '& .MuiDataGrid-row': {
                        border: 'none', // Remove the row separator lines
                    },
                    '& .MuiDataGrid-rowCount': {
                        color: 'white', // Change the background color of the selected row
                    },
                    '& .MuiDataGrid-selectedRowCount': {
                        color: 'white', // Change the font color of the selected row count
                    },
                    '& .MuiDataGrid-cell': {
                        border: 'none', // Remove the cell separator lines
                        color: 'white', // Change the font color of the cells
                        fontSize: '13px', // Make the cell text smaller
                    },
                    '& .MuiDataGrid-row:nth-of-type(odd)': {
                        backgroundColor: '#161618', // Change the background color of odd rows
                    },
                    '& .MuiDataGrid-row:nth-of-type(even)': {
                        backgroundColor: '#202020', // Change the background color of even rows
                    },
                    '& .MuiDataGrid-row:hover': {
                        backgroundColor: 'rgba(76, 146, 107, 0.1)', // Change the hover color of the row to light green with reduced opacity
                    },
                    '& .MuiDataGrid-cell--withRenderer': {
                        '& .MuiCheckbox-root': {
                            color: 'white', // Change the color of the checkbox
                            transform: 'scale(0.7)', // Make the checkbox smaller
                            '& .MuiSvgIcon-root': {
                                borderRadius: '50%', // Make the checkbox a circle
                            },
                            '&.Mui-checked': {
                                color: '#54a277', // Change the color of the selected checkbox to green
                            },
                        },
                    },
                    '& .MuiDataGrid-columnHeaderCheckbox': {
                        '& .MuiCheckbox-root': {
                            color: 'white', // Change the color of the checkbox in the column header
                            transform: 'scale(0.8)', // Make the checkbox smaller in the column header
                            '& .MuiSvgIcon-root': {
                                borderRadius: '50%', // Make the checkbox a circle in the column header
                            },
                            '&.Mui-checked': {
                                color: '#54a277', // Change the color of the selected checkbox to green in the column header
                            },
                        },
                    },
                }}
                components={{
                    LoadingOverlay: LinearProgress
                }}
                loading={lutLoading}
                rows={rows}
                columns={columns}
                pageSize={4}
                getRowId={(row) => row.id}
                // Direct text fields handle edits; no grid edit mode required
                disableRowSelectionOnClick
            ></DataGridPro>
            <ConfirmActionDialog
                open={confirmOpen}
                clickAway={() => { setConfirmOpen(false); setRowPendingDelete(null); }}
                handleClick={performDelete}
            />
        </React.Fragment>
    );
}